import React, { useState, useEffect, Suspense, lazy } from "react";
import { Route, Switch, Redirect } from "react-router-dom";
import { Routes } from "../routes";
import { usePermissions } from "../context/PermissionContext";

// components
import Sidebar from "../components/Sidebar/index.jsx";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Preloader from "../components/Preloader";
// import EntityTable from "../components/EntityTable.jsx";
// import AddProformaUser from "./proformaUser/AddProformaUser.jsx";
// import AssetPage from "./AssetPage/index.jsx";
// import AssetPageV2 from "./AssetPageV2/index.jsx";
// import ProductionReporting from "./ProductionReportPage/index.jsx";
// import UserJourney from "./UserJourney/index.jsx";
// import TrancheBot from "./TrancheBot/index.jsx";
// import AssetLogsPage from "./AssetLogs/index.jsx";

// pages
const ProjectInput = lazy(() => import("../components/ProjectInput.jsx"));
const Customization = lazy(() => import("../components/Customization.jsx"));
const NDAForm = lazy(() => import("../components/NDAForm.jsx"));
const ProformaGenerations = lazy(() =>
  import("./ProformaGenerations/index.jsx")
);
const TsaBillingPage = lazy(() => import("./TsaBilling/index.jsx"));
const TranchesPage = lazy(() => import("./Tranches/index.jsx"));
const ProposedAssetsPage = lazy(() =>
  import("./ProposedAssestsPage/index.jsx")
);
const ApprovedTranche = lazy(() => import("./ApprovedTranch/index.jsx"));
const TsaBackend = lazy(() => import("./TsaBackend/index.jsx"));
const TrancheAnalytics = lazy(() => import("./TrancheAnalytics/index.jsx"));
const ProfilePage = lazy(() => import("./Profile/index.jsx"));
const EntityTable = lazy(() => import("../components/EntityTable.jsx"));

const ContactOwner = lazy(() =>
  import("../components/ContactOwner/index.jsx").then((module) => ({
    default: module.ContactOwner,
  }))
);
const AddProformaUser = lazy(() =>
  import("./proformaUser/AddProformaUser.jsx")
);
const AssetPage = lazy(() => import("./AssetPage/index.jsx"));
const AssetPageV2 = lazy(() => import("./AssetPageV2/index.jsx"));

const AssetAudit = lazy(() => import("./AssetPendingPageV2/index.jsx")); // for asset v2 pending assets
const UserJourney = lazy(() => import("./UserJourney/index.jsx"));
const PurlMarketingResources = lazy(() =>
  import("./PurlMarketingResources/index.jsx")
);
const TrancheBot = lazy(() => import("./TrancheBot/index.jsx"));
const TrancheBotV2 = lazy(() => import("./TrancheBotV2/index.jsx"));
// const ProposedAssetsPage = lazy(() =>
//   import("./TrancheBotV2/ProposedAssetsPage")
// );
const AssetLogsPage = lazy(() => import("./AssetLogs/index.jsx"));
const DashboardOverview = lazy(() => import("./dashboard/DashboardOverview"));
const Transactions = lazy(() => import("./Transactions"));
const SubAdmin = lazy(() => import("./SubAdmin.jsx"));
const Settings = lazy(() => import("./Settings"));
const BootstrapTables = lazy(() => import("./tables/Tables.jsx"));
const Signin = lazy(() => import("./Signin.jsx"));
const ProfileSetup = lazy(() => import("./auth/ProfileSetup.jsx"));
const ForgotPassword = lazy(() => import("./auth/ForgotPassword"));
const ResetPassword = lazy(() => import("./auth/ResetPassword"));
const NotFoundPage = lazy(() => import("./auth/NotFound"));
const ServerError = lazy(() => import("./auth/ServerError"));
const NewReports = lazy(() => import("./newReport/NewReports.jsx"));
const AccessPage = lazy(() => import("./AccessPage/index.jsx"));
const ContactsPage = lazy(() => import("./Contacts/index.jsx"));
const PandCDocuments = lazy(() => import("./PandCDocuments/index.jsx"));
const ReportsCenter = lazy(() => import("./ReportsCenter/index.jsx"));
const DivisorLookup = lazy(() => import("./DivisorLookup/index.jsx"));
const AssetV2Audit = lazy(() => import("./AssetV2Audit/index.jsx"));
const ProductionReportLogsPage = lazy(() =>
  import("./ProductionReportLogs/index.jsx")
);
const TicketCenterPage = lazy(() => import("./TicketCenter/index.jsx"));
const TicketPage = lazy(() => import("./TicketPage/index.jsx"));
const PartnerDocumentsPage = lazy(() => import("./PartnerDocument/index.jsx"));

const ProductionReportsMonitoringV2 = lazy(() =>
  import("./ProductionReportMonitoringV2/index.jsx")
);
const ProductionReportingDataV2 = lazy(() =>
  import("./ProductionReportingDataV2/index.jsx")
);
const PowerBI = lazy(() => import("./PowerBI/index.jsx"));
const PowerBIPermission = lazy(() => import("./PowerBIPermission/index.jsx"));
const AssetArchiveFilesPage = lazy(() => import("./AssetArchiveFiles/index.jsx"));
const KnowledgeBasePage = lazy(() => import("./KnowledgeBase/index.jsx"));
const KnowledgeGapPage = lazy(() => import("./KnowledgeGap/index.jsx"));
const UserInteractionPage = lazy(() => import("./UserInteraction/index.jsx"));
const IrsPackagePage = lazy(() => import("./IrsPackage/index.jsx"));
const UserInteractionDetailPage = lazy(() =>
  import("./UserInteractionDetail/index.jsx")
);

export const PageLoader = () => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "70vh",
      gap: 8,
    }}
  >
    {[0, 1, 2].map((i) => (
      <div
        key={i}
        style={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          background: "#356C58",
          animation: `dotFall 1s ease-in-out ${i * 0.2}s infinite`,
        }}
      />
    ))}
    <style>{`
      @keyframes dotFall {
        0%, 80%, 100% { transform: translateY(0); opacity: 1; }
        40% { transform: translateY(12px); opacity: 0.3; }
      }
    `}</style>
  </div>
);
const M1M2DocumentsPage = lazy(() => import("./M1M2Documents/index.jsx"));
const MasterUsersPage = lazy(() => import("./MasterUsers/index.jsx"));

const RouteWithLoader = ({ component: Component, permission, ...rest }) => {
  const [loaded, setLoaded] = useState(false);
  const { hasPermission } = usePermissions();

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Route
      {...rest}
      render={(props) => {
        // Only check permission if one is specified
        if (permission && !hasPermission(permission)) {
          return <Redirect to="/dashboard/overview" />;
        }

        return (
          <>
            {rest.location?.pathname === "/new-reports/edit" ? (
              <Preloader show={loaded ? false : true} />
            ) : (
              <></>
            )}
            <Suspense fallback={<PageLoader />}>
              <Component {...props} />
            </Suspense>
          </>
        );
      }}
    />
  );
};

const RouteWithSidebar = ({ component: Component, permission, ...rest }) => {
  const { hasPermission } = usePermissions();
  const localStorageIsSettingsVisible = () => {
    return localStorage.getItem("settingsVisible") === "false" ? false : true;
  };

  const [showSettings, setShowSettings] = useState(
    localStorageIsSettingsVisible
  );

  const toggleSettings = () => {
    setShowSettings(!showSettings);
    localStorage.setItem("settingsVisible", !showSettings);
  };

  return (
    <Route
      {...rest}
      render={(props) => {
        // Only check permission if one is specified
        if (permission && !hasPermission(permission)) {
          return <Redirect to="/dashboard/overview" />;
        }

        return (
          <>
            <Sidebar />
            <main className="content">
              <Navbar />
              <Suspense fallback={<PageLoader />}>
                <Component {...props} />
              </Suspense>
              <Footer
                toggleSettings={toggleSettings}
                showSettings={showSettings}
              />
            </main>
          </>
        );
      }}
    />
  );
};

export default () => (
  <Switch>
    <RouteWithLoader exact path={Routes.Signin.path} component={Signin} />
    <RouteWithLoader
      exact
      path={Routes.ProfileSetup.path}
      component={ProfileSetup}
    />
    <RouteWithLoader
      exact
      path={Routes.ForgotPassword.path}
      component={ForgotPassword}
    />
    <RouteWithLoader
      exact
      path={Routes.ResetPassword.path}
      component={ResetPassword}
    />
    <RouteWithLoader
      exact
      path={Routes.NotFound.path}
      component={NotFoundPage}
    />
    <RouteWithLoader
      exact
      path={Routes.ServerError.path}
      component={ServerError}
    />

    {/* Dashboard - might need to be accessible to everyone with a token */}
    <RouteWithSidebar
      exact
      path={Routes.DashboardOverview.path}
      component={DashboardOverview}
    />

    <RouteWithSidebar
      exact
      path={Routes.Profile.path}
      component={ProfilePage}
    // permission="read:customization_section"
    />
    <RouteWithSidebar
      exact
      path={Routes.Customization.path}
      component={Customization}
      permission="read:customization_section"
    />
    <RouteWithSidebar
      exact
      path={Routes.SubAdmin.path}
      component={SubAdmin}
      permission="read:users"
    />
    <RouteWithSidebar
      exact
      path={Routes.Transactions.path}
      component={Transactions}
      permission="read:report_list"
    />
    <RouteWithSidebar exact path={Routes.Settings.path} component={Settings} />
    <RouteWithSidebar
      exact
      path={Routes.BootstrapTables.path}
      component={BootstrapTables}
      permission="read:proforma_backend"
    />
    <RouteWithSidebar
      exact
      path={Routes.NewReports.path}
      component={NewReports}
      permission="read:new_reports"
    />
    <RouteWithSidebar
      exact
      path={Routes.EntityTable.path}
      component={EntityTable}
      permission="read:entity_table"
    />

    <RouteWithSidebar
      exact
      path={Routes.ContactOwner.path}
      component={ContactOwner}
      permission="read:contact_owner"
    />
    <RouteWithSidebar
      exact
      path={Routes.ProjectInput.path}
      component={ProjectInput}
      permission="read:project_input"
    />
    <RouteWithSidebar
      exact
      path={Routes.ProformaUsers.path}
      component={AddProformaUser}
      permission="read:proforma_users"
    />
    <RouteWithSidebar
      exact
      path={Routes.Assets.path}
      component={AssetPage}
      permission="read:do_not_use_-_assets_v1"
    />
    <RouteWithSidebar
      exact
      path={Routes.AssetsV2.path}
      component={AssetPageV2}
      permission="read:assets_v2"
    />
    <RouteWithSidebar
      exact
      path={Routes.AssetAudit.path}
      component={AssetAudit}
      permission="read:assets_audit"
    />

    <RouteWithSidebar
      exact
      path={Routes.AssetsUnderwriting.path}
      component={AssetV2Audit}
      permission="read:assets_underwriting"
    />
    <RouteWithSidebar
      exact
      path={Routes.TsaBackend.path}
      component={TsaBackend}
      permission="read:tsa_backend"
    />
    <RouteWithSidebar
      exact
      path={Routes.Tranches.path}
      component={TranchesPage}
      permission="read:tranches"
    />
    <RouteWithSidebar
      exact
      path={Routes.AssetLogs.path}
      component={AssetLogsPage}
      permission="read:asset_logs"
    />
    <RouteWithSidebar
      exact
      path={Routes.ProformaGenerations.path}
      component={ProformaGenerations}
      permission="read:proforma_generations"
    />
    {/* <RouteWithSidebar
      exact
      path={Routes.ProductionMonitoringAndReporting.path}
      component={ProductionReportsMonitoring}
      permission="read:production_monitoring_and_reporting"
    /> */}
    <RouteWithSidebar
      exact
      path={Routes.ProductionMonitoringAndReporting.path}
      component={ProductionReportsMonitoringV2}
      permission="read:production_monitoring_and_reporting"
    />
    {/* <RouteWithSidebar
      exact
      path={Routes.ProductionReportingData.path}
      component={ProductionReportingData}
      permission="read:production_monitoring_data"
    /> */}
    {/* <RouteWithSidebar
      exact
      path={Routes.ProductionReporting.path}
      component={ProductionReporting}
      permission="read:production_reporting"
    /> */}
    <RouteWithSidebar
      exact
      path={Routes.ProductionReportingData.path}
      component={ProductionReportingDataV2}
      permission="read:production_monitoring_data"
    />
    {/* <RouteWithSidebar
      exact
      path={Routes.ProductionReporting.path}
      component={ProductionReportingV2}
      permission="read:production_reporting"
    /> */}
    <RouteWithSidebar
      exact
      path={Routes.ProductionReportLogs.path}
      component={ProductionReportLogsPage}
      permission="read:production_report_logs"
    />
    <RouteWithSidebar
      exact
      path={Routes.NDAForm.path}
      component={NDAForm}
      permission="read:nda_form"
    />
    <RouteWithSidebar
      exact
      path={Routes.USERJourney.path}
      component={UserJourney}
      permission="read:user_journey"
    />
    <RouteWithSidebar
      exact
      path={Routes.PurlMarketingResources.path}
      component={PurlMarketingResources}
      permission="read:marketing"
    />
    <RouteWithSidebar
      exact
      path={Routes.AccessControl.path}
      component={AccessPage}
      permission="read:access_control"
    />
    <RouteWithSidebar
      exact
      path={Routes.TrancheBot.path}
      component={TrancheBot}
      permission="read:tranche_bot"
    />
    <RouteWithSidebar
      exact
      path={Routes.TrancheBotV2.path}
      component={TrancheBotV2}
      permission="read:tranche_bot_v2"
    />
    <RouteWithSidebar
      exact
      path={Routes.ProposedAssets.path}
      component={ProposedAssetsPage}
      permission="read:tranche_bot_v2"
    />
    <RouteWithSidebar
      exact
      path={Routes.ApprovedTranche.path}
      component={ApprovedTranche}
      permission="read:tranche_bot_v2"
    />
    <RouteWithSidebar
      exact
      path={Routes.TrancheAnalytics.path}
      component={TrancheAnalytics}
      permission="read:tranche_bot_v2"
    />
    <RouteWithSidebar
      exact
      path={Routes.TsaBilling.path}
      component={TsaBillingPage}
      permission="read:tsa_billing"
    />
    <RouteWithSidebar
      exact
      path={Routes.Contacts.path}
      component={ContactsPage}
      permission="read:contacts"
    />
    <RouteWithSidebar
      exact
      path={Routes.PartnerDocument.path}
      component={PartnerDocumentsPage}
      permission="read:partner_documents"
    />
    <RouteWithSidebar
      exact
      path={Routes.PandCDocuments.path}
      component={PandCDocuments}
      permission="read:p&c_documents"
    />

    <RouteWithSidebar
      exact
      path={Routes.ReportsCenter.path}
      component={ReportsCenter}
      permission="read:reports_center"
    />
    <RouteWithSidebar
      exact
      path={Routes.DivisorLookup.path}
      component={DivisorLookup}
      permission="read:divisor_lookup"
    />
    <RouteWithSidebar
      exact
      path={Routes.TicketCenter.path}
      component={TicketCenterPage}
      permission="read:ticket_center"
    />
    <RouteWithSidebar
      exact
      path={Routes.TicketPage.path}
      component={TicketPage}
      permission="read:ticket_center"
    />

    <RouteWithSidebar
      exact
      path={Routes.PowerBI.path}
      component={PowerBI}
      permission="read:power_bi"
    />

    <RouteWithSidebar
      exact
      path={Routes.PowerBIPermission.path}
      component={PowerBIPermission}
      permission="read:power_bi_permission"
    />

    <RouteWithSidebar
      exact
      path={Routes.KnowledgeBase.path}
      component={KnowledgeBasePage}
      permission="read:knowledge_base"
    />

    <RouteWithSidebar
      exact
      path={Routes.KnowledgeGap.path}
      component={KnowledgeGapPage}
      permission="read:knowledge_gap"
    />

    <RouteWithSidebar
      exact
      path={Routes.UserInteraction.path}
      component={UserInteractionPage}
      permission="read:user_interaction"
    />
    <RouteWithSidebar
      exact
      path={Routes.UserInteractionDetail.path}
      component={UserInteractionDetailPage}
      permission="read:user_interaction"
    />
    <RouteWithSidebar
      exact
      path={Routes.M1M2Documents.path}
      component={M1M2DocumentsPage}
      permission="read:m1_&_m2_documents"
    />
    <RouteWithSidebar
      exact
      path={Routes.AssetArchiveFiles.path}
      component={AssetArchiveFilesPage}
      permission="read:asset_archive_files"
    />
    <RouteWithSidebar
      exact
      path={Routes.MasterUsers.path}
      component={MasterUsersPage}
      permission="read:master_users"
    />
    <RouteWithSidebar
      exact
      path={Routes.IrsPackage.path}
      component={IrsPackagePage}
      permission="read:irs_package"
    />

    {/* Default route */}
    {localStorage.getItem("token") ? (
      <Redirect to={Routes.DashboardOverview.path} />
    ) : (
      <Redirect to={Routes.Signin.path} />
    )}
  </Switch>
);
